import React, { useMemo, useCallback } from "react";
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  LineSeries,
  Tooltip,
  Legend,
  Inject,
} from "@syncfusion/ej2-react-charts";
import { formatNumber } from "../../utils/formatNumber";


const COLOR_PALETTE = [
  "#3498DB", "#57096C", "#595a4a", "#489668", "#FDC004",
  "#000000", "#EA4335", "#34A953", "#008080", "#ff6347",
  "#4682b4", "#32cd32", "#ff4500", "#1e90ff",
  "#ff1493", "#8a2be2", "#dc143c", "#ff8c00", "#7cfc00",
  "#9932cc", "#b22222", "#5f9ea0"
];

export default function PastPerformanceChart({
  labels = [],
  seriesSets = [],
  title = "Past Performance",
  empty = false,
}) {


  const storedUser = JSON.parse(
  localStorage.getItem("adm_panel_user") || "{}"
  );

  const isDark = !!storedUser.darkmode;

  const axisTextColor = isDark ? "#ffffff" : "#666666";
  /* ---------- normalize data ---------- */

  const chartSets = useMemo(() => {
    return seriesSets.map((s) => ({
      name: s.name,
      data: s.data.map((y, i) => ({
        x: i,
        y,
        label: labels[i],
      })),
    }));
  }, [seriesSets, labels]);

  const maxY = useMemo(() => {
    const all = seriesSets.flatMap((s) => s.data);
    const max = Math.max(...all, 0);
    return max > 0 ? Math.ceil(max * 1.12) : 10;
  }, [seriesSets]);

  /* ---------- axis labels (every 15 minutes) ---------- */

  const handleAxisLabelRender = useCallback(
    (args) => {
      if (args.axis?.name !== "primaryXAxis") return;

      const idx = Math.round(Number(args.value));
      const label = labels[idx];

      if (!label) {
        args.text = "";
        return;
      }

        const parts = label.split(":");
        const mm = Number(parts[1]);

        if (mm % 15 !== 0) {
        args.text = "";
        return;
        }

        args.text = label;

    },
    [labels]
  );

  /* ---------- tooltip ---------- */

  const handleTooltipRender = useCallback(
    (args) => {
      const idx = Math.round(args.point.x);
      const label = labels[idx] ?? "";
      args.text = `${label} — ${formatNumber(args.point.y)}`;
    },
    [labels]
  );

  return (
    <div className="chart-container">
      <h3 className="chart-title">{title}</h3>

      <ChartComponent
        height="430px"
        background="transparent"
        primaryXAxis={{
          valueType: "Double",
          minimum: 0,
          maximum: Math.max(labels.length - 1, 0),
          interval: 10,
          labelIntersectAction: "Rotate45",
          majorGridLines: { width: 0 },
          majorTickLines: { width: 0 },
          labelStyle: {
            fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
            color: axisTextColor
          },
        }}
        primaryYAxis={{
          name: "primaryYAxis",
          minimum: 0,
          maximum: maxY,
          labelFormat: "n0",
          majorTickLines: { width: 0 },
          lineStyle: { width: 0 },
          labelStyle: {
            fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
            color: axisTextColor
          },
        }}
        tooltip={{ enable: true }}
        legendSettings={{
          visible: true,
          position: "Top",
          alignment: "Center",
          textStyle: {
            fontFamily: "Helvetica Neue, Helvetica, Arial, sans-serif",
          },
        }}
        axisLabelRender={handleAxisLabelRender}
        tooltipRender={handleTooltipRender}
      >
        <Inject services={[LineSeries, Tooltip, Legend]} />

        <SeriesCollectionDirective>
          {!empty &&
            chartSets.map((s, i) => (
              <SeriesDirective
                key={s.name}
                dataSource={s.data}
                xName="x"
                yName="y"
                type="Line"
                name={s.name}
                width={2.5}
                fill={COLOR_PALETTE[i % COLOR_PALETTE.length]}
                border={{
                  width: 2.5,
                  color: COLOR_PALETTE[i % COLOR_PALETTE.length],
                }}
                marker={{ visible: false }}
                animation={{ enable: true, duration: 700 }}
              />
            ))}
        </SeriesCollectionDirective>
      </ChartComponent>
    </div>
  );
}
